import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {catchError, Observable, shareReplay, throwError} from 'rxjs';
import {PaginationInterface} from '../../interfaces/responses/pagination.interface';
import {
  AdminQueryParams,
  CreateAdminRequest,
  UpdateAdminRequest,
} from '../../interfaces/requests/admin.interfaces';
import {Admin, AdminResponse} from '../../interfaces/responses/admin-response.interfaces';
import {ApiKey, ApiKeyQueryParams, ApiKeysResponse} from '../../interfaces/responses/api-keys.interface';
import {GenerateApiKeyRequest} from '../../interfaces/requests/api-keys.interfaces';
import {Role} from '../../interfaces/responses/user-model.interfaces';
import {BusinessDevelopmentUser} from '../../interfaces/responses/business-development-user.interface';
import {toHttpParams} from '../../utils/app.util';

const env = environment;

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private http = inject(HttpClient);
  private businessDevelopmentUsers$: Observable<BusinessDevelopmentUser[]> | null = null;

  getBusinessDevelopmentUsers(): Observable<BusinessDevelopmentUser[]> {
    if (!this.businessDevelopmentUsers$) {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        token = localStorage.getItem('tokenn');
      }
      const url = `${env.apiUrl}/admins/business-development-users`;
      this.businessDevelopmentUsers$ = this.http
        .get<BusinessDevelopmentUser[]>(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .pipe(
          shareReplay(1),
          catchError((error) => {
            this.businessDevelopmentUsers$ = null;
            return throwError(() => error);
          })
        );
    }
    return this.businessDevelopmentUsers$;
  }

  getAdmins(
    queryParams: AdminQueryParams,
  ): Observable<PaginationInterface<Admin[]>> {
    let token: string | null = null;

    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/admins`;

    // Always set paginate to true
    const params = {
      ...queryParams,
      paginate: true,
    };

    return this.http
      .get<PaginationInterface<Admin[]>>(url, {
        params: toHttpParams(params),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  getRoles(): Observable<Role[]> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/roles`;
    return this.http
      .get<Role[]>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  createAdmin(data: CreateAdminRequest): Observable<AdminResponse> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/admins`;
    return this.http
      .post<AdminResponse>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  updateAdmin(
    id: string,
    data: UpdateAdminRequest
  ): Observable<AdminResponse> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/admins/${id}`;
    return this.http
      .put<AdminResponse>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  deleteAdmin(id: string): Observable<void> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/admins/${id}`;
    return this.http
      .delete<void>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  toggleStatus(id: string): Observable<AdminResponse> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/admins/${id}/toggle-status`;
    return this.http
      .patch<AdminResponse>(url, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  getApiKeys(
    queryParams: ApiKeyQueryParams,
  ): Observable<ApiKeysResponse> {
    let token: string | null = null;

    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/api-keys`;

    // Always set paginate to true
    const params = {
      ...queryParams,
      paginate: true,
    };

    return this.http
      .get<ApiKeysResponse>(url, {
        params: toHttpParams(params),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  updateApiKeyOrigins(id: string, allowedOrigins: string[]): Observable<ApiKey> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/api-keys/${id}/origins`;
    return this.http
      .patch<ApiKey>(url, { allowedOrigins }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  generateApiKey(payload: GenerateApiKeyRequest): Observable<ApiKey> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/api-keys`;
    return this.http
      .post<ApiKey>(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }
}

